import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { User, UserRole, LoginCredentials } from "../../types/auth";
import { AuthService } from "../../services/AuthService";
import { SessionExpiredModal } from "./SessionExpiredModal";
import PageLoading from "../Common/PageLoading";
import {
  bootstrapSessionOnce,
  refreshSessionIfDue,
  startProactiveSessionRefresh,
} from "../../services/sessionRefresh";

// The logged-in user comes from the Next.js login cookie (GraphQL { me }), so an
// Admin/Supervisor signed in there gets their real role here. Set
// REACT_APP_SESSION_CHECK=false to skip it (local dev without the cookie).
const SESSION_CHECK_ENABLED = process.env.REACT_APP_SESSION_CHECK !== "false";
const PUBLIC_PATHS = [
  "/login",
  "/admin/login",
  "/supervisor/login",
  "/demo-login",
];

interface AuthContextType {
  user: User | null;
  role: UserRole | null;
  isAuthenticated: boolean;
  login: (credentials: LoginCredentials) => Promise<User>;
  logout: () => void;
  switchRole: (role: UserRole) => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const DummyAuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [sessionExpired, setSessionExpired] = useState<boolean>(false);
  const { pathname } = useLocation();
  const isPublicPath = PUBLIC_PATHS.includes(pathname);

  const verifySession = useCallback(async () => {
    const session = await AuthService.checkSession();
    if (session.status === "valid") {
      setUser(session.user);
      setSessionExpired(false);
    } else if (session.status === "expired") {
      AuthService.logout();
      setSessionExpired(true);
    } else {
      // GraphQL unreachable: the session state is unknown, keep the last known
      // user. With none, stay signed out - never fall back to the mock user.
      setUser((prev) => prev || AuthService.getStoredUser());
    }
  }, []);

  useEffect(() => {
    if (!SESSION_CHECK_ENABLED) {
      setUser(AuthService.getCurrentUser());
      setLoading(false);
      return;
    }
    // Access token is 15 minutes. Renew on a 12 minute cadence. `me` does not
    // renew it. Tab focus re-reads the user and refreshes only if that window
    // has already elapsed (hidden tabs freeze the timer).
    bootstrapSessionOnce();
    const stopRefresh = startProactiveSessionRefresh();
    verifySession().finally(() => setLoading(false));

    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      void refreshSessionIfDue().then(() => verifySession());
    };
    document.addEventListener("visibilitychange", onVisible);
    const stopListening = AuthService.onSessionExpired(() => {
      AuthService.logout();
      setSessionExpired(true);
    });
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      stopRefresh();
      stopListening();
    };
  }, [verifySession]);

  const handleLogin = async (credentials: LoginCredentials): Promise<User> => {
    const authenticatedUser = await AuthService.login(credentials);
    setUser(authenticatedUser);
    setSessionExpired(false);
    return authenticatedUser;
  };

  const handleLogout = () => {
    AuthService.logout();
    setUser(null);
  };

  const handleSwitchRole = async (targetRole: UserRole) => {
    const allUsers = await AuthService.getUsers();
    const targetUser = allUsers.find((u) => u.role === targetRole);
    if (targetUser) {
      await handleLogin({
        email: targetUser.email,
        password: targetUser.password,
      });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        role: user ? user.role : null,
        isAuthenticated: !!user,
        login: handleLogin,
        logout: handleLogout,
        switchRole: handleSwitchRole,
        loading,
      }}
    >
      {loading && !isPublicPath ? (
        <PageLoading />
      ) : sessionExpired && !isPublicPath ? (
        <>
          {/* Expired mid-use: keep the page behind the popup. No user yet: show nothing. */}
          {user && children}
          <SessionExpiredModal neverSignedIn={!user} />
        </>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within a DummyAuthProvider");
  }
  return context;
};
